import React, { useState } from 'react';

interface CreateBatimentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BatimentFormData) => void;
  sites: Array<{ id: string; code: string; nom: string }>;
  editingBatiment?: any;
}

export interface BatimentFormData {
  nom: string;
  code: string;
  nombreEtages: number;
  siteId: string;
}

const CreateBatimentModal: React.FC<CreateBatimentModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  sites,
  editingBatiment
}) => {
  const [formData, setFormData] = useState<BatimentFormData>({
    nom: '',
    code: '',
    nombreEtages: 1,
    siteId: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Pré-remplir le formulaire en mode édition
  React.useEffect(() => {
    if (editingBatiment) {
      setFormData({
        nom: editingBatiment.nom || '',
        code: editingBatiment.code || '',
        nombreEtages: editingBatiment.nombreEtages || 1,
        siteId: editingBatiment.siteId || '',
      });
    } else {
      setFormData({
        nom: '',
        code: '',
        nombreEtages: 1,
        siteId: '',
      });
    }
  }, [editingBatiment, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.code.trim()) newErrors.code = 'Le trigramme est obligatoire';
    if (!formData.siteId) newErrors.siteId = 'Le site est obligatoire';
    if (formData.nombreEtages < 1) newErrors.nombreEtages = 'Le nombre d\'étages doit être au moins 1';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      setFormData({ nom: '', code: '', nombreEtages: 1, siteId: '' });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">🏗️ {editingBatiment ? 'Modifier le Bâtiment' : 'Nouveau Bâtiment'}</h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                {/* Site */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="siteId"
                    value={formData.siteId}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.siteId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Sélectionner un site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.nom} ({site.code})
                      </option>
                    ))}
                  </select>
                  {errors.siteId && <p className="mt-1 text-sm text-red-600">{errors.siteId}</p>}
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du bâtiment <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nom ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: B1 (Bâtiment 1)"
                  />
                  {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                </div>

                {/* Trigramme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigramme / Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: ROB1, ROB2"
                    maxLength={10}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                </div>

                {/* Nombre d'étages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre d'étages <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="nombreEtages"
                    value={formData.nombreEtages}
                    onChange={handleChange}
                    min="1"
                    max="20"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nombreEtages ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.nombreEtages && <p className="mt-1 text-sm text-red-600">{errors.nombreEtages}</p>}
                  <p className="mt-1 text-xs text-gray-500">RDC compte comme étage 0</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
{editingBatiment ? 'Modifier le bâtiment' : 'Créer le bâtiment'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBatimentModal;

